import { useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { CommunityTemplateGalleryRead } from '../../types/api';
import { listCommunityTemplates } from './communityTemplatesApi';

export interface CommunityGalleryResult {
  templates: CommunityTemplateGalleryRead[];
  isLoading: boolean;
  error: string | null;
}

/**
 * The approved community templates, most used first.
 *
 * Loaded once per mount like the pack registry. It is not this business's data
 * — it is everybody's — so there is nothing here to keep in step with a local
 * write; a template the member just published appears once an operator has
 * approved it, which is a page load away by definition.
 */
export function useCommunityGallery(): CommunityGalleryResult {
  const [templates, setTemplates] = useState<CommunityTemplateGalleryRead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    // isLoading starts true and this effect runs once, so no synchronous
    // setState is needed before the fetch settles.
    listCommunityTemplates(controller.signal)
      .then((loaded) => {
        setTemplates(loaded);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(getErrorMessage(caught, 'The community gallery did not load. Reload to retry.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, []);

  return { templates, isLoading, error };
}
